import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ReportDateRangePicker, Button, Alert, LoadingSpinner } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FiFilter, FiRefreshCw, FiGlobe, FiBriefcase } from 'react-icons/fi';
import { MdOutlineSummarize, MdPendingActions } from 'react-icons/md';
import {
  FaFileContract, FaSadTear, FaChartLine, FaWeightHanging,
  FaTrophy, FaProjectDiagram, FaClipboardCheck, FaBoxOpen,
  FaStream, FaUserFriends, FaUserPlus
} from 'react-icons/fa';
import { formatNumberWord } from 'helpers/utils';

import SalesFilterSidebar from 'components/app/crm/dashboard/sales/SalesFilterSidebar';
import ConversionFunnelChart from 'components/app/crm/dashboard/sales/ConversionFunnelChart';
import LostProjectsChart from 'components/app/crm/dashboard/sales/LostProjectsChart';
import JobEntryCategoryChart from 'components/app/crm/dashboard/job-entries/JobEntryCategoryChart';
import JobEntryDomesticOverseaChart from 'components/app/crm/dashboard/job-entries/JobEntryDomesticOverseaChart';
import QuotationTrendChart from 'components/app/crm/dashboard/quotations/QuotationTrendChart';
import QuotationBreakdownChart from 'components/app/crm/dashboard/quotations/QuotationBreakdownChart';
import OverviewKpiCards from './OverviewKpiCards';
import SummaryCard from './components/SummaryCard';
import StatTile from './components/StatTile';
import { BarList, MonthlyBars } from './components/MiniCharts';
import OverviewDetailGrid from './OverviewDetailGrid';
import DetailGridDrawer from './DetailGridDrawer';
import PcvTab from './tabs/PcvTab';
import QuotationTab from './tabs/QuotationTab';
import ContractTab from './tabs/ContractTab';
import WaiverTab from './tabs/WaiverTab';
import {
  fetchOverview,
  fetchOverviewFilterOptions,
  fetchTabData,
  resetError
} from 'redux/crm/dashboard/overviewSlice';

const DEFAULT_START = dayjs().startOf('year').format('YYYY-MM-DD');
const DEFAULT_END   = dayjs().format('YYYY-MM-DD');

const fmt = (v) => formatNumberWord(Number(v) || 0);

const CrmOverviewDashboard = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Shared trend grouping for the per-tab trend charts (month / quarter / year).
  const [tabGroupBy, setTabGroupBy] = useState('month');
  const [drillDown, setDrillDown] = useState({ open: false, section: 'sales', title: '', extraParams: {} });

  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('start_date') || DEFAULT_START,
    endDate:   searchParams.get('end_date')   || DEFAULT_END
  });

  const [filters, setFilters] = useState({
    sales_units:     searchParams.get('sales_units')?.split(',').filter(Boolean).map(Number) || [],
    sales_incharges: searchParams.get('sales_incharges')?.split(',').filter(Boolean) || [],
    customers:       searchParams.get('customers')?.split(',').filter(Boolean).map(Number) || [],
    statuses:        searchParams.get('statuses')?.split(',').filter(Boolean).map(Number) || [],
    q_j_number:      searchParams.get('q_j_number') || ''
  });

  const { overview, filterOptions, tabs } = useSelector(state => state.crm_dashboard_crm_overview);
  const o = overview.data || {};

  const buildParams = useCallback(() => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units;
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges;
    if (filters.customers.length)       p.customers       = filters.customers;
    if (filters.statuses.length)        p.statuses        = filters.statuses;
    if (filters.q_j_number?.trim())     p.q_j_number      = filters.q_j_number.trim();
    return p;
  }, [dateRange, filters]);

  const baseParams = useMemo(() => buildParams(), [buildParams]);

  // Sync URL
  useEffect(() => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units.join(',');
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges.join(',');
    if (filters.customers.length)       p.customers       = filters.customers.join(',');
    if (filters.statuses.length)        p.statuses        = filters.statuses.join(',');
    if (filters.q_j_number?.trim())     p.q_j_number      = filters.q_j_number.trim();
    setSearchParams(p);
  }, [dateRange, filters, setSearchParams]);

  // Fetch overview on filter change
  useEffect(() => {
    dispatch(fetchOverview(baseParams));
  }, [dispatch, baseParams]);

  // Filter options are the full selectable UNIVERSE, fetched once — never narrowed by
  // the user's own selection (else picking a sales unit hides all the others and you
  // must Reset to get them back).
  useEffect(() => {
    dispatch(fetchOverviewFilterOptions({}));
  }, [dispatch]);

  // Lazy-load the active tab's analytical bundle; refetch when the trend
  // grouping (month/quarter/year) changes so the trend charts update.
  useEffect(() => {
    if (['overview', 'pcv', 'quotation', 'contract', 'waiver'].includes(activeTab)) {
      dispatch(fetchTabData({ ...baseParams, tab: activeTab, group_by: tabGroupBy }));
    }
  }, [dispatch, baseParams, activeTab, tabGroupBy]);

  const openDrill = useCallback(({ section, title, extraParams }) => {
    setDrillDown({ open: true, section: section || 'sales', title: title || 'Details', extraParams: extraParams || {} });
  }, []);
  const closeDrill = useCallback(() => setDrillDown(prev => ({ ...prev, open: false })), []);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setSidebarOpen(false);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({ sales_units: [], sales_incharges: [], customers: [], statuses: [], q_j_number: '' });
  }, []);

  const handleRefresh = useCallback(() => {
    dispatch(resetError());
    dispatch(fetchOverview(baseParams));
  }, [dispatch, baseParams]);

  const activeFilterCount =
    filters.sales_units.length + filters.sales_incharges.length +
    filters.customers.length + filters.statuses.length + (filters.q_j_number?.trim() ? 1 : 0);

  // Query string forwarded to detailed dashboards (date range + sales units)
  const navQuery = useMemo(() => {
    const qs = new URLSearchParams({ start_date: dateRange.startDate, end_date: dateRange.endDate });
    if (filters.sales_units.length) qs.set('sales_units', filters.sales_units.join(','));
    return `?${qs.toString()}`;
  }, [dateRange, filters.sales_units]);

  const job = o.job_entry || {};
  const con = o.contract  || {};
  const sal = o.sales     || {};
  const lost = o.lost     || {};

  // Job Entry analytics + "who signed contracts" snapshot (lazy 'overview' tab bundle).
  const jobTab        = tabs?.overview?.data || {};
  const jobTabLoading = tabs?.overview?.loading;
  const topCustomers  = (jobTab.top_customers || []).slice(0, 5)
    .map(c => ({ label: c.customer_name, value: c.grand_total, sub: `${c.contract_count || 0} contracts` }));
  // Lead Source detail — from-lead projects per month.
  const leadTrendItems = (jobTab.lead_trend || []).map(t => ({ label: t.period_label, value: t.from_lead_count }));
  const lostMonthly = lost.monthly || [];
  const maxMonthlyLost = Math.max(1, ...lostMonthly.map(m => Number(m.lost) || 0));

  // Lead-source split (which projects came from leads)
  const fromLead = Number(sal.from_lead_count) || 0;
  const direct   = Number(sal.direct_count) || 0;
  const leadTotal = fromLead + direct;
  const leadPct  = leadTotal > 0 ? Math.round((fromLead / leadTotal) * 100) : 0;

  const TABS = [
    { key: 'overview',  label: 'Overview',  icon: MdOutlineSummarize },
    { key: 'pcv',       label: 'PCV',       icon: FaStream },
    { key: 'quotation', label: 'Quotation', icon: MdPendingActions },
    { key: 'contract',  label: 'Contract',  icon: FaFileContract },
    { key: 'waiver',    label: 'Waiver',    icon: FaClipboardCheck },
    { key: 'lost',      label: 'Lost',      icon: FaSadTear }
  ];

  const tabPane = (key, children) => (
    <div className={activeTab === key ? '' : 'tw-hidden'}>
      {children}
    </div>
  );

  const content = (
    <>
      <OverviewKpiCards data={o} onDrill={openDrill} />

      {/* ── BOD pipeline hero: creation → quotation → negotiation → won/lost ── */}
      <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3">
        <div className="tw-col-span-12 xl:tw-col-span-8">
          <ConversionFunnelChart
            data={o.funnel}
            loading={overview.loading && !overview.data}
            subtitle="Project creation → Quotation → Negotiation → Won (click a stage for details)"
            onDrillDown={(title, extra) => openDrill({ section: 'sales', title, extraParams: extra })}
          />
        </div>
        <div className="tw-col-span-12 xl:tw-col-span-4">
          <div className="tw-bg-white tw-h-full" style={{ boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.075)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', padding: '1rem 1.25rem' }}>
              <h5 className="tw-mb-0 tw-font-bold tw-text-slate-700">Lead Source</h5>
              <small className="tw-text-slate-500">Where projects originate</small>
            </div>
            <div className="tw-p-3 tw-flex tw-flex-col tw-justify-center">
              <div className="tw-grid tw-grid-cols-12 tw-gap-3">
                <div className="tw-col-span-6">
                  <div role="button" className="tw-cursor-pointer" onClick={() => openDrill({ section: 'sales', title: 'From Lead — Projects', extraParams: { from_lead: 'true' } })}>
                    <StatTile icon={FaUserPlus} theme="indigo" label="From Lead" value={fmt(fromLead)} sub={`${leadPct}% of total`} />
                  </div>
                </div>
                <div className="tw-col-span-6">
                  <div role="button" className="tw-cursor-pointer" onClick={() => openDrill({ section: 'sales', title: 'Direct — Projects', extraParams: { from_lead: 'false' } })}>
                    <StatTile icon={FaUserFriends} theme="blue" label="Direct" value={fmt(direct)} sub={`${100 - leadPct}% of total`} />
                  </div>
                </div>
              </div>
              <div className="tw-mt-3">
                <div className="tw-flex tw-justify-between tw-text-sm tw-text-slate-600 tw-mb-1">
                  <span>Lead contribution</span><span className="tw-font-bold">{leadPct}%</span>
                </div>
                <div className="tw-bg-slate-200 tw-rounded-xl" style={{ height: 10, overflow: 'hidden' }}>
                  <div style={{ width: `${leadPct}%`, height: '100%', background: 'linear-gradient(90deg, #667eea, #2563eb)' }} />
                </div>
              </div>
              {leadTrendItems.length > 0 && (
                <div className="tw-mt-3">
                  <div className="tw-text-xs tw-text-slate-500 tw-mb-1">From-lead projects / month</div>
                  <MonthlyBars items={leadTrendItems} valueKey="value" color="#6366f1" height={90} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-white" style={{ boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.075)', borderRadius: 12 }}>
        {/* Tab bar stays pinned below the page header while scrolling the report */}
        <div
          className="tw-bg-white tw-border-b tw-border-solid tw-border-slate-200 tw-shadow-sm"
          style={{ position: 'sticky', top: 'calc(var(--bs-top-nav-height, 4.3125rem) + 70px)', zIndex: 30, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
        >
          <div className="tw-flex tw-items-center tw-flex-nowrap tw-overflow-x-auto" role="tablist">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                onClick={() => setActiveTab(key)}
                className={`tw-flex tw-items-center tw-gap-1 tw-py-3 tw-px-3 tw-text-sm tw-font-semibold tw-cursor-pointer tw-bg-transparent tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-b-2 tw-border-solid tw-select-none tw-whitespace-nowrap tw-outline-none focus:tw-outline-none ${
                  activeTab === key
                    ? 'tw-text-blue-600 tw-border-b-blue-600'
                    : 'tw-text-slate-600 hover:tw-text-slate-800 tw-border-b-transparent'
                }`}
              >
                <Icon />
                <span className="tw-mb-0 tw-ml-1">{label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="tw-bg-slate-50 tw-p-4">
          {overview.loading && !overview.data ? (
            <div className="tw-text-center tw-py-12">
              <LoadingSpinner message="Loading overview…" />
            </div>
          ) : (
            <>
              {/* ── Overview: Job Entry + Contract headline ── */}
              {tabPane('overview', (
                <>
                  <div className="tw-grid tw-grid-cols-12 tw-gap-3">
                    <div className="tw-col-span-12 xl:tw-col-span-6">
                      <SummaryCard title="Job Entry" subtitle="Performance metrics overview"
                        linkTo={`/dashboard/crm/job-entry${navQuery}`}>
                        <div className="tw-col-span-6 md:tw-col-span-4"><StatTile icon={FaProjectDiagram} theme="indigo" label="Projects" value={fmt(job.total)} sub={`vs ${fmt(job.prev_total)}`} /></div>
                        <div className="tw-col-span-6 md:tw-col-span-4"><StatTile icon={FaWeightHanging} theme="blue" label="Weight (M.T)" value={fmt(job.total_weight)} sub={`vs ${fmt(job.prev_total_weight)}`} /></div>
                        <div className="tw-col-span-6 md:tw-col-span-4"><StatTile icon={FaTrophy} theme="green" label="Became Job" value={`${fmt(job.became_job)}/${fmt(job.total)}`} sub={`${job.became_job_rate ?? 0}% conv.`} /></div>
                      </SummaryCard>
                    </div>
                    <div className="tw-col-span-12 xl:tw-col-span-6">
                      <SummaryCard title="Contract" subtitle="Signed contracts overview"
                        linkTo={`/dashboard/crm/contract${navQuery}`}>
                        <div className="tw-col-span-6"><StatTile icon={FaFileContract} theme="indigo" label="Contracts" value={fmt(con.total_contracts)} sub={`${fmt(con.project_count)} projects`} /></div>
                        <div className="tw-col-span-6"><StatTile icon={FaChartLine} theme="green" label="Value (USD)" value={fmt(con.grand_total_value)} /></div>
                        <div className="tw-col-span-6"><StatTile icon={FaWeightHanging} theme="blue" label="Weight (M.T)" value={fmt(con.total_weight)} /></div>
                        <div className="tw-col-span-6"><StatTile icon={FaBoxOpen} theme="rose" label="Unreleased" value={fmt(con.pending_count)} /></div>
                      </SummaryCard>
                    </div>
                  </div>

                  {/* ── Job Entry analytics (full chart suite) ── */}
                  <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mt-1">
                    <div className="tw-col-span-12 xl:tw-col-span-6"><JobEntryCategoryChart data={jobTab.category} loading={jobTabLoading} /></div>
                    <div className="tw-col-span-12 xl:tw-col-span-6"><JobEntryDomesticOverseaChart data={jobTab.domestic_oversea} loading={jobTabLoading} /></div>
                  </div>
                  <div className="tw-mt-3">
                    <QuotationTrendChart title="Job Entry Trend" data={jobTab.trend} loading={jobTabLoading} groupBy={tabGroupBy} onGroupByChange={setTabGroupBy} />
                  </div>
                  <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mt-1">
                    <div className="tw-col-span-12 lg:tw-col-span-6">
                      <QuotationBreakdownChart title="Job Entry — By Region" Icon={FiGlobe} iconColor="tw-text-emerald-500"
                        rowLabel="Region" labelKey="region" drillKey="region" drillTitlePrefix="Region"
                        data={jobTab.region} loading={jobTabLoading} fullHeight />
                    </div>
                    <div className="tw-col-span-12 lg:tw-col-span-6">
                      <QuotationBreakdownChart title="Job Entry — By Sales Unit" Icon={FiBriefcase} iconColor="tw-text-blue-500"
                        rowLabel="Sales Unit" labelKey="unit_name" subLabelKey="region" drillKey="unit_name" drillTitlePrefix="Sales Unit"
                        data={jobTab.sales_unit} loading={jobTabLoading} fullHeight />
                    </div>
                  </div>

                  {/* ── Who signed contracts (top customers by signed value) ── */}
                  <div className="tw-mt-3">
                    <SummaryCard title="Top Customers — Signed Contracts" subtitle="Customers converting to signed contracts (by grand total value)"
                      linkTo={`/dashboard/crm/contract${navQuery}`}>
                      <div className="tw-col-span-12"><BarList items={topCustomers} color="#059669" valueFmt={fmt} emptyText="No signed contracts in this period." /></div>
                    </SummaryCard>
                  </div>

                  <div className="tw-mt-3">
                    <OverviewDetailGrid section="sales" title="All Projects" baseParams={baseParams} />
                  </div>
                </>
              ))}

              {/* ── PCV: pipeline stages + monthly + region + detail ── */}
              {tabPane('pcv', (
                <PcvTab data={tabs?.pcv?.data} loading={tabs?.pcv?.loading} baseParams={baseParams} onDrill={openDrill} groupBy={tabGroupBy} onGroupByChange={setTabGroupBy} />
              ))}

              {/* ── Quotation ── */}
              {tabPane('quotation', (
                <QuotationTab data={tabs?.quotation?.data} loading={tabs?.quotation?.loading} baseParams={baseParams} onDrill={openDrill} groupBy={tabGroupBy} onGroupByChange={setTabGroupBy} />
              ))}

              {/* ── Contract ── */}
              {tabPane('contract', (
                <ContractTab data={tabs?.contract?.data} loading={tabs?.contract?.loading} baseParams={baseParams} groupBy={tabGroupBy} onGroupByChange={setTabGroupBy} />
              ))}

              {/* ── Waiver ── */}
              {tabPane('waiver', (
                <WaiverTab data={tabs?.waiver?.data} loading={tabs?.waiver?.loading} baseParams={baseParams} />
              ))}

              {/* ── Lost: split + top reasons (chart) · monthly trend · by region ── */}
              {tabPane('lost', (
                <>
                  {/* Quotation/Job lost split + top reasons */}
                  <div className="tw-mb-3">
                    <LostProjectsChart
                      data={lost}
                      loading={overview.loading && !overview.data}
                      onDrillDown={(title, extra) => openDrill({ section: 'lost', title, extraParams: { funnel_stage: 'lost', ...extra } })}
                    />
                  </div>

                  <div className="tw-grid tw-grid-cols-12 tw-gap-3">
                    {/* Monthly lost trend */}
                    <div className="tw-col-span-12 lg:tw-col-span-6">
                      <div className="tw-bg-white tw-h-full" style={{ boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.075)', borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', padding: '1rem 1.25rem' }}>
                          <h5 className="tw-mb-0 tw-font-bold tw-text-slate-700">Lost by Month</h5>
                          <small className="tw-text-slate-500">Lost projects per month in the selected period</small>
                        </div>
                        <div className="tw-p-3">
                          {lostMonthly.length === 0 ? (
                            <p className="tw-text-slate-500 tw-text-sm tw-italic tw-mb-0">No data.</p>
                          ) : (
                            <div className="tw-flex tw-items-end tw-justify-between tw-gap-2" style={{ height: 160 }}>
                              {lostMonthly.map((m) => (
                                <div key={m.label} className="tw-flex tw-flex-col tw-items-center tw-flex-1" title={`${m.label}: ${m.lost} lost / ${m.total} total`}>
                                  <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-mb-1">{m.lost}</span>
                                  <div className="tw-rounded-t-sm" style={{ width: '70%', height: `${(Number(m.lost) / maxMonthlyLost) * 120}px`, minHeight: 2, background: 'linear-gradient(180deg, #f43f5e, #e11d48)' }} />
                                  <span className="tw-text-xs tw-text-slate-500 tw-mt-1 tw-truncate" style={{ maxWidth: 48 }}>{m.label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Lost & quotation by region — chart/table toggle + Count/Job/Weight metric */}
                    <div className="tw-col-span-12 lg:tw-col-span-6">
                      <QuotationBreakdownChart
                        title="By Region · Quotation vs Lost" Icon={FiGlobe} iconColor="tw-text-rose-500"
                        rowLabel="Region" labelKey="region" drillKey="region" drillTitlePrefix="Region"
                        data={lost.by_region || []} loading={overview.loading && !overview.data} fullHeight
                      />
                    </div>
                  </div>
                  <div className="tw-mt-3">
                    <OverviewDetailGrid section="lost" title="Lost Projects" baseParams={baseParams} extraParams={{ funnel_stage: 'lost' }} />
                  </div>
                </>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="tw-min-h-screen tw-bg-slate-50">
      {/* ── Standard sticky header (consistent with the other CRM dashboards) ── */}
      <div className="tw-sticky tw-z-40 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm" style={{ top: 'var(--bs-top-nav-height, 4.3125rem)' }}>
        <div className="tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <div className="tw-flex-1 tw-min-w-0">
            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">CRM Overview</h1>
            <p className="tw-text-xs tw-text-gray-400 tw-hidden md:tw-block">
              Pipeline · PCV · Quotation · Contract · Waiver · Lost · by selected period
            </p>
          </div>

          <ReportDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
          />

          {/* Filters toggle — only needed on mobile; sidebar is fixed on md+ */}
          <Button
            onClick={() => setSidebarOpen(open => !open)}
            variant="secondary"
            className="md:tw-hidden"
          >
            <FiFilter size={14} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-600 tw-text-white tw-text-xs tw-font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>

          <Button onClick={handleRefresh} variant="ghost" className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-500 hover:tw-text-gray-800">
            <FiRefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {overview.error && (
        <div className="tw-px-5 tw-pt-4">
          <Alert type="error" message="Failed to load the overview. Try refreshing." dismissible />
        </div>
      )}

      {/* ── Main layout: fixed left filter sidebar + content ── */}
      <div className="tw-flex tw-gap-5 tw-px-5 tw-py-5">
        <div className={`tw-flex-shrink-0 tw-w-64 tw-transition-all tw-duration-300 ${sidebarOpen ? 'tw-block' : 'tw-hidden md:tw-block'}`}>
          <div className="tw-sticky tw-top-20">
            <SalesFilterSidebar
              filterOptions={filterOptions.data}
              loading={filterOptions.loading}
              filters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              containerHeight="max(320px, calc(100dvh - 200px))"
            />
          </div>
        </div>

        <div className="tw-flex-1 tw-min-w-0">
          {overview.loading && !overview.data ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-32 tw-gap-3">
              <LoadingSpinner message="Loading dashboard… this can take a moment" />
            </div>
          ) : content}
        </div>
      </div>

      {/* Drill-down detail drawer (opened from KPIs / funnel / charts) */}
      <DetailGridDrawer
        open={drillDown.open}
        section={drillDown.section}
        title={drillDown.title}
        baseParams={baseParams}
        extraParams={drillDown.extraParams}
        onClose={closeDrill}
      />
    </div>
  );
};

export default CrmOverviewDashboard;
